/**
 * Conversation detail page — live transcript drill-down.
 *
 * Fetches conversation metadata + transcript turns from tenant blob storage
 * via the admin API. Only available for drilldown_allowed workspaces.
 */

import { useState, useEffect } from 'react';
import { useWorkspace } from '../hooks/useWorkspace';
import * as api from '../api-client';
import { AdminPage, AdminPanel, AdminPageHeader } from '../components/AdminPrimitives';

interface Props {
  conversationId: string;
  onBack: () => void;
  onOpenPrevious?: () => void;
  onOpenNext?: () => void;
  hasPreviousConversation?: boolean;
  hasNextConversation?: boolean;
}

export function ConversationDetailPage({
  conversationId,
  onBack,
  onOpenPrevious,
  onOpenNext,
  hasPreviousConversation = false,
  hasNextConversation = false,
}: Props) {
  const { workspace } = useWorkspace();
  const [conversation, setConversation] = useState<api.ConversationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspace) return;
    let cancelled = false;

    async function load() {
      try {
        const res = await api.getConversation(workspace!.workspace_key, conversationId);
        if (!cancelled) {
          setConversation(res);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof api.AdminApiError ? err.detail : 'Failed to load conversation',
          );
          setIsLoading(false);
        }
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [workspace, conversationId]);

  if (!workspace) return null;

  return (
    <AdminPage className="max-w-4xl">
      <div>
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          <BackIcon />
          Back to conversations
        </button>
      </div>

      <AdminPageHeader
        title="Conversation Detail"
        subtitle="Transcript content is fetched live from tenant storage when this page opens."
      />

      {error && (
        <div role="alert" className="mb-4 p-3 text-sm rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-[var(--status-error)]">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          <div className="h-28 rounded-[1.4rem] bg-[var(--surface-tertiary)] animate-pulse" />
          <div className="h-72 rounded-[1.4rem] bg-[var(--surface-tertiary)] animate-pulse" />
        </div>
      ) : conversation ? (
        <>
          <AdminPanel
            title="Conversation Metadata"
            subtitle="High-level identifiers and routing metadata for this transcript."
            className="mb-6"
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs font-medium text-[var(--text-tertiary)]">Conversation ID</p>
                <p className="text-sm font-mono text-[var(--text-primary)] mt-0.5 truncate">
                  {conversation.conversation_id}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-[var(--text-tertiary)]">Started</p>
                <p className="text-sm text-[var(--text-primary)] mt-0.5">
                  {formatTimestamp(conversation.started_at)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-[var(--text-tertiary)]">Turns</p>
                <p className="text-sm text-[var(--text-primary)] mt-0.5">{conversation.turn_count}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-[var(--text-tertiary)]">Model</p>
                <p className="text-sm text-[var(--text-primary)] mt-0.5">
                  {conversation.routing_tier ?? '—'}
                </p>
              </div>
            </div>
            {conversation.intent_summary && (
              <div className="mt-3 pt-3 border-t border-[var(--border-subtle)]">
                <p className="text-xs font-medium text-[var(--text-tertiary)]">Intent</p>
                <p className="text-sm text-[var(--text-primary)] mt-0.5">
                  {conversation.intent_summary}
                </p>
              </div>
            )}
          </AdminPanel>

          <AdminPanel
            title={`Transcript (${conversation.turns.length} turns)`}
            subtitle="Messages are left-aligned and labeled by speaker so reviewers can scan the exchange quickly."
            rightSlot={
              <div className="flex items-center gap-2">
                <TranscriptNavButton
                  label="Prev conversation"
                  onClick={onOpenPrevious}
                  disabled={!onOpenPrevious || !hasPreviousConversation}
                />
                <TranscriptNavButton
                  label="Next conversation"
                  onClick={onOpenNext}
                  disabled={!onOpenNext || !hasNextConversation}
                />
              </div>
            }
          >
            <div className="space-y-4">
              {conversation.turns.map((turn, i) => (
                <div key={i} className="space-y-2">
                  {turn.user_message && (
                    <div className="flex justify-start">
                      <div className="max-w-[80%] rounded-[1.4rem] border border-[var(--border-default)] bg-white px-4 py-3 text-[var(--text-primary)] shadow-[0_10px_26px_rgba(15,23,42,0.04)]">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)]">
                          User
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{turn.user_message}</p>
                        {turn.timestamp && (
                          <p className="mt-2 text-xs text-[var(--text-tertiary)]">
                            {formatTimestamp(turn.timestamp)}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {turn.assistant_message && (
                    <div className="flex justify-start">
                      <div className="max-w-[80%] rounded-[1.4rem] border border-[var(--border-default)] bg-[var(--accent-subtle)] px-4 py-3 text-[var(--text-primary)] shadow-[0_10px_26px_rgba(15,23,42,0.04)]">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)]">
                          Assistant
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{turn.assistant_message}</p>
                        <div className="mt-2 flex items-center gap-2 text-xs text-[var(--text-tertiary)]">
                          {turn.model_used && <span>{turn.model_used}</span>}
                          {turn.tokens_used != null && <span>{turn.tokens_used} tokens</span>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {conversation.turns.length === 0 && (
              <div className="py-8 text-center text-[var(--text-secondary)]">
                No transcript turns available.
              </div>
            )}
          </AdminPanel>
        </>
      ) : null}
    </AdminPage>
  );
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function BackIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function TranscriptNavButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick?: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center rounded-lg border border-[var(--border-default)] bg-[var(--surface-secondary)] px-3 py-1.5 text-sm font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--surface-tertiary)] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {label}
    </button>
  );
}
